import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("admin@attestra.io");
  const [password, setPassword] = useState("Admin@12345");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const r = await login(email, password);
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      toast.error(r.error);
      return;
    }
    toast.success(`Welcome back, ${r.user.name}`);
    const to = location.state?.from || "/app";
    navigate(to, { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between bg-[#0A1128] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-30" />
        <div className="relative">
          <Link to="/" className="flex items-center" data-testid="auth-logo">
            <Logo onDark className="h-8" />
          </Link>
        </div>
        <div className="relative">
          <blockquote className="text-2xl font-medium leading-snug tracking-tight">
            “Attestra replaced six tools and two compliance headaches. Every agreement is now
            audit-ready on day one.”
          </blockquote>
          <div className="mt-6 text-sm text-white/60">
            <div>K. Patel · VP, People Operations</div>
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center p-6 sm:p-12">
        <div className="max-w-md w-full mx-auto">
          <h1 className="text-3xl font-bold tracking-tight">Sign in to Attestra</h1>
          <p className="mt-2 text-sm text-slate-500">
            Welcome back. Enter your credentials to access your workspace.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Work email
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-emerald-700 hover:text-emerald-800 font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                testIdToggle="login-password-toggle"
                className="mt-1.5 h-11"
              />
            </div>

            {error && (
              <div
                className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-slate-500 text-center">
            Don't have a workspace?{" "}
            <Link
              to="/register"
              className="font-medium text-slate-900 hover:text-emerald-700"
              data-testid="goto-signup-link"
            >
              Create one
            </Link>
          </div>

          <div className="mt-10 flex items-center gap-2 text-xs text-slate-500 justify-center">
            <Lock className="h-3.5 w-3.5" />
            Sessions are protected with httpOnly cookies.
          </div>
        </div>
      </div>
    </div>
  );
}
